import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { Button } from '@/components/ui'
import { useSEO } from '@/hooks/useSEO'
import { useScrollReveal, useHeroReveal } from '@gjl/motion/css'
import { generateBreadcrumbs, breadcrumbLabels } from '@/components/Breadcrumbs'
import { Check, ArrowRight, Music, Palette, Code, Users } from 'lucide-react'

export default function AboutPage() {
  const breadcrumbs = generateBreadcrumbs('/about', breadcrumbLabels)

  // Animation refs
  const heroRef = useHeroReveal<HTMLDivElement>(0.2)
  const teamRef = useScrollReveal<HTMLDivElement>('.team-card', { stagger: 0.15, delay: 0.4 })
  const benefitsRef = useScrollReveal<HTMLDivElement>('.benefit-item', { stagger: 0.08 })
  const ctaRef = useScrollReveal<HTMLDivElement>('.cta-content')

  useSEO({
    title: 'About',
    description:
      'Meet the team behind Fugue Lab—a musician and software engineer helping classical music organizations build modern websites and digital systems.',
    url: '/about',
    canonical: '/about',
    breadcrumbs,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: 'About Fugue Lab',
      description: 'Learn about Fugue Lab and our mission to help classical music organizations thrive digitally.',
      mainEntity: {
        '@type': 'Organization',
        name: 'Fugue Lab',
        url: 'https://fuguelab.com',
      },
    },
  })

  return (
    <div className="text-foreground mx-auto max-w-4xl space-y-12 px-4 py-12">
      {/* Hero Section */}
      <div ref={heroRef} className="space-y-4 text-center">
        <h1 className="text-fluid-h1">About Fugue Lab</h1>
        <p className="text-muted-foreground mx-auto max-w-2xl text-lg leading-relaxed">
          We exist to serve classical music organizations with exceptional web design, digital strategy, and audience
          engagement systems—so you can focus on the music.
        </p>
      </div>

      {/* Team Members */}
      <div ref={teamRef} className="grid gap-8 md:grid-cols-2">
        {/* Garrett */}
        <motion.div
          className="team-card group border-border/50 from-primary/5 hover:border-primary/30 relative rounded-2xl border bg-gradient-to-b to-transparent p-6 transition-all duration-300 hover:shadow-lg"
          whileHover={{ y: -4 }}
          transition={{ type: 'spring', stiffness: 300, damping: 20 }}
        >
          <div className="flex flex-col items-center space-y-4 text-center">
            <div className="border-primary/20 h-28 w-28 overflow-hidden rounded-full border-2 shadow-lg">
              <img
                src="/headshot.jpg"
                alt="Garrett John Law"
                width={112}
                height={112}
                className="h-full w-full object-cover object-[center_30%]"
              />
            </div>
            <div>
              <h2 className="text-xl font-semibold">Garrett John Law</h2>
              <p className="text-primary text-sm font-medium">Founder & Developer</p>
            </div>
            <div className="flex gap-2">
              <span className="bg-primary/10 text-primary inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs">
                <Music className="h-3 w-3" /> Organist
              </span>
              <span className="bg-primary/10 text-primary inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs">
                <Code className="h-3 w-3" /> Engineer
              </span>
            </div>
          </div>
          <div className="text-muted-foreground mt-4 space-y-3 text-sm">
            <p>
              Professional organist, church music director, and software engineer. Director of Music at Holy Trinity
              Episcopal Church in Covina, California.
            </p>
            <p>
              Interlochen alum and Cleveland Institute of Music graduate with 4+ years building production software.
            </p>
          </div>
        </motion.div>

        {/* Danielle */}
        <motion.div
          className="team-card group border-border/50 from-primary/5 hover:border-primary/30 relative rounded-2xl border bg-gradient-to-b to-transparent p-6 transition-all duration-300 hover:shadow-lg"
          whileHover={{ y: -4 }}
          transition={{ type: 'spring', stiffness: 300, damping: 20 }}
        >
          <div className="flex flex-col items-center space-y-4 text-center">
            <div className="border-primary/20 bg-primary flex h-28 w-28 items-center justify-center rounded-full border-2 shadow-lg">
              <span className="text-primary-foreground text-3xl font-bold">DD</span>
            </div>
            <div>
              <h2 className="text-xl font-semibold">Danielle Dinstman</h2>
              <p className="text-primary text-sm font-medium">Creative Director</p>
            </div>
            <div className="flex gap-2">
              <span className="bg-primary/10 text-primary inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs">
                <Palette className="h-3 w-3" /> Designer
              </span>
              <span className="bg-primary/10 text-primary inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs">
                <Users className="h-3 w-3" /> Educator
              </span>
            </div>
          </div>
          <div className="text-muted-foreground mt-4 space-y-3 text-sm">
            <p>
              Artist, designer, and the creative force behind Fugue Lab's visual identity work. BA and MFA from Boston
              University, UCLA Extension Communication Design certificate.
            </p>
            <p>Currently teaches art at Sierra Canyon School, helping students develop their creative vision.</p>
          </div>
        </motion.div>
      </div>

      {/* Why Work With Us */}
      <div
        ref={benefitsRef}
        className="from-primary/10 via-primary/5 border-primary/20 space-y-6 rounded-2xl border bg-gradient-to-br to-transparent p-8"
      >
        <h2 className="text-fluid-h3">Why Work With Us?</h2>
        <div className="grid gap-4 md:grid-cols-2">
          {[
            'Built by a working musician who understands classical music from the inside',
            'Proven track record with festivals, church music programs, and more',
            'Complete digital strategy: websites, ticketing, applications, donor management',
            'Designs that balance artistry with conversion and performance',
            'Technical expertise combined with real-world music program experience',
          ].map((item, idx) => (
            <motion.div
              key={idx}
              className="benefit-item flex items-start gap-3"
              whileHover={{ x: 4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <div className="mt-0.5 flex-shrink-0">
                <Check className="text-primary h-5 w-5" strokeWidth={2.5} />
              </div>
              <p className="text-muted-foreground leading-relaxed">{item}</p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div
        ref={ctaRef}
        className="cta-content border-border/50 from-primary/5 space-y-6 rounded-2xl border bg-gradient-to-b to-transparent p-8 text-center"
      >
        <h2 className="text-fluid-h3">Ready to Modernize Your Digital Presence?</h2>
        <p className="text-muted-foreground mx-auto max-w-xl">
          Whether you need a new website, a ticketing or application system, ongoing digital support, or a complete
          digital strategy—we're here to help.
        </p>
        <div className="flex flex-col justify-center gap-4 pt-2 sm:flex-row">
          <a href="https://calendly.com/garrett-garrettjohnlaw/30min" target="_blank" rel="noopener noreferrer">
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            >
              <Button size="lg" className="group cursor-pointer px-8">
                Book a Free Call
                <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Button>
            </motion.div>
          </a>
          <Link to="/contact">
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            >
              <Button variant="outline" size="lg" className="cursor-pointer px-8">
                Or Send Us a Message
              </Button>
            </motion.div>
          </Link>
        </div>
      </div>
    </div>
  )
}
